/**
 * CR-7782: the Call Off Management list's columns, per role, and the viewer's
 * saved layout of them.
 *
 * Each role is offered the columns it has data for. The supplier is never
 * offered the haulage terms (cost, agreed delivery date, lorry number): the
 * backend withholds them from its payload, so they would be columns of dashes.
 * CR-9072: the truck type is not one of them — the supplier loads the truck.
 * The haulier is not offered the Haulier column, which on its list is always
 * itself.
 *
 * The layout — which columns show, and in what order — is a per-viewer
 * convenience, so it lives in localStorage under the user and the role rather
 * than costing a round trip. Every read and write is guarded, because a
 * private window or blocked site data makes storage throw rather than return
 * nothing.
 *
 * @link useCallOffColumns for the hook the list reads this through.
 */

/** Whose list this is: the columns, their labels and the defaults differ. */
export type CallOffListRole = 'customer' | 'supplier' | 'haulier';

export interface CallOffColumn {
  id: string;
  label: string;
  /** Always shown: the row's identity, and how it is opened. */
  fixed?: boolean;
  /** Offered in the picker, but off until the viewer asks for it. */
  defaultHidden?: boolean;
  align?: 'right';
}

/** Which columns show and in what order, as stored for one viewer. */
export interface CallOffColumnLayout {
  order: string[];
  hidden: string[];
}

interface ColumnSpec {
  id: string;
  label: string | Record<CallOffListRole, string>;
  fixed?: boolean;
  align?: 'right';
  /** The roles offered this column, and whether it starts hidden for each. */
  roles: Partial<Record<CallOffListRole, 'shown' | 'hidden'>>;
}

const ALL_SHOWN = { customer: 'shown', supplier: 'shown', haulier: 'shown' } as const;

/** Every list column, in default order. */
const COLUMN_SPECS: readonly ColumnSpec[] = [
  { id: 'reference', label: 'Reference', fixed: true, roles: ALL_SHOWN },
  // The other side: the supplier to a customer or a haulier (who collects
  // FROM the supplier), the customer to a supplier.
  {
    id: 'party',
    label: { customer: 'Supplier', supplier: 'Customer', haulier: 'Supplier' },
    roles: ALL_SHOWN,
  },
  // The date the customer set on the call-off. The haulier's own collection
  // date sits beside it as "Pickup Date".
  { id: 'pickup_date', label: 'Expected Pickup Date', roles: ALL_SHOWN },
  { id: 'haulier_pickup_date', label: 'Pickup Date', roles: ALL_SHOWN },
  { id: 'destination', label: 'Destination', roles: ALL_SHOWN },
  { id: 'fill_percent', label: 'Load Fill', align: 'right', roles: ALL_SHOWN },
  { id: 'detail_status', label: 'Status', roles: ALL_SHOWN },
  // CR-7732: the haulier's own figures. Off by default for the haulier, whose
  // list is for planning collections; shown to the customer, who pays them.
  {
    id: 'haulage_cost', label: 'Haulage Cost', align: 'right',
    roles: { customer: 'shown', haulier: 'hidden' },
  },
  {
    id: 'agreed_delivery_date', label: 'Agreed Delivery Date',
    roles: { customer: 'shown', haulier: 'hidden' },
  },
  { id: 'haulier', label: 'Haulier', roles: { customer: 'hidden', supplier: 'hidden' } },
  // CR-9072: Standard or Mega — shown to all three, the supplier loads it.
  { id: 'truck_type', label: 'Truck Type', roles: ALL_SHOWN },
  { id: 'lorry_number', label: 'Lorry No', roles: { customer: 'hidden', haulier: 'hidden' } },
  { id: 'created_date', label: 'Created Date', roles: ALL_SHOWN },
  { id: 'last_edited', label: 'Last Edited', roles: ALL_SHOWN },
];

/**
 * The columns one role is offered, in default order.
 *
 * @param role - whose list it is
 * @returns the role's columns with their labels and defaults resolved
 */
export function columnsForRole(role: CallOffListRole): CallOffColumn[] {
  return COLUMN_SPECS.flatMap((spec) => {
    const start = spec.roles[role];
    if (!start) return [];
    const label = typeof spec.label === 'string' ? spec.label : spec.label[role];
    return [{
      id: spec.id, label, fixed: spec.fixed, align: spec.align,
      defaultHidden: start === 'hidden',
    }];
  });
}

/**
 * The layout a viewer starts with, and goes back to on Reset.
 *
 * @param role - whose list it is
 * @returns default order, and the role's off-by-default columns hidden
 */
export function defaultLayout(role: CallOffListRole): CallOffColumnLayout {
  const columns = columnsForRole(role);
  return {
    order: columns.map((c) => c.id),
    hidden: columns.filter((c) => c.defaultHidden).map((c) => c.id),
  };
}

const STORAGE_PREFIX = 'callOff.columns';
/** CR-7732's haulier-only key: an array of hidden ids, not per user. */
const LEGACY_HAULIER_KEY = 'callOff.hiddenColumns';
/** The columns that existed when the legacy key was written. */
const LEGACY_COLUMN_IDS = [
  'reference', 'party', 'pickup_date', 'destination', 'fill_percent', 'detail_status',
  'haulage_cost', 'agreed_delivery_date', 'created_date', 'last_edited',
];

function storageKey(role: CallOffListRole, userId: number | string | undefined): string {
  return `${STORAGE_PREFIX}:${role}:${userId ?? 'anonymous'}`;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * Fit a stored layout to the role's current columns.
 *
 * Ids the role no longer has are dropped (a supplier never keeps a haulage
 * column). A column the stored layout has never seen is placed after its
 * default predecessor and takes its default visibility, so a column added in
 * a later release appears where it belongs. Fixed columns never hide.
 *
 * @param role - whose list it is
 * @param stored - what storage held, of unknown shape
 * @returns a complete, valid layout
 */
export function normaliseLayout(role: CallOffListRole, stored: unknown): CallOffColumnLayout {
  const columns = columnsForRole(role);
  const known = new Set(columns.map((c) => c.id));
  const raw = (stored && typeof stored === 'object') ? stored as Record<string, unknown> : {};
  const order = Array.from(new Set(stringArray(raw['order']).filter((id) => known.has(id))));
  const seen = new Set(order);
  const hidden = new Set(stringArray(raw['hidden']).filter((id) => seen.has(id)));

  columns.forEach((column, index) => {
    if (seen.has(column.id)) return;
    const before = columns.slice(0, index).reverse().find((c) => order.includes(c.id));
    order.splice(before ? order.indexOf(before.id) + 1 : 0, 0, column.id);
    if (column.defaultHidden) hidden.add(column.id);
  });
  columns.filter((c) => c.fixed).forEach((c) => hidden.delete(c.id));

  return { order, hidden: order.filter((id) => hidden.has(id)) };
}

/**
 * The viewer's saved layout for this role, or the default.
 *
 * @param role - whose list it is
 * @param userId - the signed-in user, so a shared browser keeps each layout apart
 * @returns a complete, valid layout
 */
export function loadLayout(role: CallOffListRole, userId?: number | string): CallOffColumnLayout {
  try {
    const raw = window.localStorage.getItem(storageKey(role, userId));
    if (raw !== null) return normaliseLayout(role, JSON.parse(raw));
    // A haulier's choice from before layouts were per user (CR-7732).
    const legacy = role === 'haulier' ? window.localStorage.getItem(LEGACY_HAULIER_KEY) : null;
    if (legacy !== null) {
      return normaliseLayout(role, { order: LEGACY_COLUMN_IDS, hidden: JSON.parse(legacy) });
    }
  } catch {
    // Private window, blocked site data, or a value someone else wrote.
  }
  return defaultLayout(role);
}

/**
 * Remember the viewer's layout for this role.
 *
 * @param role - whose list it is
 * @param userId - the signed-in user
 * @param layout - the layout to keep
 */
export function saveLayout(
  role: CallOffListRole,
  userId: number | string | undefined,
  layout: CallOffColumnLayout,
): void {
  try {
    window.localStorage.setItem(storageKey(role, userId), JSON.stringify(layout));
  } catch {
    // Not being able to remember the choice is not worth failing the page.
  }
}

/**
 * CR-7732: the API ordering field behind each sortable column.
 *
 * Sorting is asked of the server, not done in the browser: the list is
 * paginated, so reordering the rows on screen would say "sorted" while the row
 * that belongs first sat on page three. A column absent from here is not
 * sortable — Actions has nothing to sort by.
 *
 * @param columnId - the column the header belongs to
 * @param partyIsSupplier - whether the party column shows the supplier (the
 *   customer's and the haulier's view) rather than the customer
 * @returns the `ordering` value to send, or null when the column cannot sort
 */
export function orderingFieldFor(
  columnId: string,
  partyIsSupplier: boolean,
): string | null {
  if (columnId === 'party') return partyIsSupplier ? 'supplier__name' : 'customer__name';
  const map: Record<string, string> = {
    reference: 'reference',
    pickup_date: 'pickup_date',
    haulier_pickup_date: 'haulier_pickup_date',
    destination: 'destination_name',
    fill_percent: 'fill_percent',
    detail_status: 'detail_status',
    haulage_cost: 'haulage_cost',
    agreed_delivery_date: 'haulier_agreed_delivery_date',
    haulier: 'haulier__name',
    lorry_number: 'lorry_number',
    truck_type: 'truck_type',
    created_date: 'created_at',
    last_edited: 'last_edited_at',
  };
  return map[columnId] ?? null;
}
